import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { enhancedServices } from "../../services/enhancedCrudService";
import { apiService } from "../../services/apiService";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import Pagination from "../../components/Pagination";

const TileList = () => {
  const [tileProducts, setTileProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pdfViewerModal, setPdfViewerModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  const fetchTileProducts = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: itemsPerPage,
        ordering: "-uploaded_at",
      };

      const response = await enhancedServices.tile.getList(params);

      if (
        response.results &&
        response.results.status === "success" &&
        response.results.products
      ) {
        setTileProducts(response.results.products || []);
        setProductCount(
          response.count || response.results.products.length || 0
        );
        setTotalPages(
          Math.ceil(
            (response.count || response.results.products.length || 0) /
              itemsPerPage
          )
        );
        setCurrentPage(page);
      } else {
        toast.error(response.message || "Failed to fetch tile products");
      }
    } catch (error) {
      console.error("Error fetching tile products:", error);
      if (error.response?.data?.detail) {
        toast.error(`API Error: ${error.response.data.detail}`);
      } else {
        toast.error("Failed to fetch tile products");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTileProductDetail = async (productId) => {
    setDetailLoading(true);
    try {
      const endpoint = API_ENDPOINTS.TILES.DETAIL(productId);
      const response = await apiService.get(endpoint);

      if (response && response.status === "success") {
        return response.data || response;
      } else {
        toast.error(response.message || "Failed to fetch product details");
        return null;
      }
    } catch (error) {
      console.error("Error fetching tile product detail:", error);
      if (error.response?.data?.detail) {
        toast.error(`API Error: ${error.response.data.detail}`);
      } else {
        toast.error("Failed to fetch product details");
      }
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetailModal = async (product) => {
    setDetailModal(true);
    setDetailLoading(true);
    setSelectedProduct(product);
    const detailedProduct = await fetchTileProductDetail(product.product_id);

    if (detailedProduct) {
      setSelectedProduct({
        ...product,
        ...detailedProduct,
      });
    }
  };

  const handlePageChange = (page) => {
    fetchTileProducts(page);
  };

  const closeDetailModal = () => {
    setDetailModal(false);
    setSelectedProduct(null);
  };

  const openPdfViewer = (pdf) => {
    setSelectedPdf(pdf);
    setPdfViewerModal(true);
  };

  const closePdfViewer = () => {
    setPdfViewerModal(false);
    setSelectedPdf(null);
  };

  useEffect(() => {
    fetchTileProducts();
  }, []);

  return (
    <div>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Tile Products</h3>
            <p className="text-muted mb-0">
              Manage tile products and their details.
            </p>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Products</h5>
              <span className="badge bg-primary">Total: {productCount}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-3">Loading tile products...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Manufacturing Company</th>
                        <th>Firm Name</th>
                        <th>Size</th>
                        <th>Grade</th>
                        <th>PDFs</th>
                        <th>Status</th>
                        <th>Published</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tileProducts.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center py-5">
                            <i className="fe fe-package fe-3x text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-2">
                              No tile products found
                            </p>
                            <small className="text-muted">
                              Get started by creating your first tile product
                            </small>
                          </td>
                        </tr>
                      ) : (
                        tileProducts.map((product, index) => (
                          <tr key={index}>
                            <td>
                              {index + 1 + (currentPage - 1) * itemsPerPage}
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {product.manufacturing_company_name ||
                                    "No Company"}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>
                                  {product.seller_firm_name ||
                                    "N/A"}
                                </strong>
                              </div>
                            </td>
                            <td>{product.tile_size || "N/A"}</td>
                            <td>{product.tile_grade || "N/A"}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {product.pdf_list &&
                                product.pdf_list.length > 0 ? (
                                  product.pdf_list.map((pdf, pdfIndex) => (
                                    <a
                                      key={pdfIndex}
                                      href={pdf.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                      title={pdf.alt || `PDF ${pdfIndex + 1}`}
                                    >
                                      <i className="fe fe-file-text"></i>
                                      <span className="ms-1">PDF</span>
                                    </a>
                                  ))
                                ) : null}
                                
                                {product.link ? (
                                  <a
                                    href={product.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-success"
                                    title="Product Link"
                                  >
                                    <i className="fe fe-external-link"></i>
                                    <span className="ms-1">Link</span>
                                  </a>
                                ) : null}
                                
                                {(!product.pdf_list || product.pdf_list.length === 0) && !product.link ? (
                                  <span className="text-muted">No PDFs/link</span>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  product.is_active ? "bg-success" : "bg-danger"
                                }`}
                              >
                                {product.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  product.is_published
                                    ? "bg-info"
                                    : "bg-secondary"
                                }`}
                              >
                                {product.is_published ? "Published" : "Draft"}
                              </span>
                            </td>
                            <td>
                              <div className="actions d-flex flex-wrap gap-1">
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => openDetailModal(product)}
                                  title="View Details"
                                >
                                  <i className="fe fe-eye"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                loading={loading}
                showPageInfo={true}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {detailModal && selectedProduct && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Product Details</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDetailModal}
                ></button>
              </div>
              <div className="modal-body">
                {detailLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3">
                      Loading product details...
                    </p>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <h4>
                        {selectedProduct.manufacturing_company_name ||
                          "No Company"}
                      </h4>
                      <p className="text-muted">
                        {selectedProduct.category} - {selectedProduct.tile_type}
                      </p>

                      <div className="row mt-3">
                        <div className="col-sm-6">
                          <h6>Basic Information</h6>
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Manufacturing Company Name:</strong>
                                </td>
                                <td>{selectedProduct.manufacturing_company_name || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Description:</strong>
                                </td>
                                <td>{selectedProduct.description || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Tile Size:</strong>
                                </td>
                                <td>
                                  {selectedProduct.tile_size_name ||
                                    selectedProduct.tile_size ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Tile Type:</strong>
                                </td>
                                <td>
                                  {selectedProduct.tile_type_name ||
                                    selectedProduct.tile_type ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Tile Grade:</strong>
                                </td>
                                <td>{selectedProduct.tile_grade || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Tile Body:</strong>
                                </td>
                                <td>{selectedProduct.tile_body || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Thickness:</strong>
                                </td>
                                <td>
                                  {selectedProduct.thickness_mm
                                    ? `${selectedProduct.thickness_mm} mm`
                                    : "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Box Weight:</strong>
                                </td>
                                <td>
                                  {selectedProduct.box_weight_kg
                                    ? `${selectedProduct.box_weight_kg} kg`
                                    : "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-sm-6">
                          <h6>Additional Details</h6>
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>PCS per Box:</strong>
                                </td>
                                <td>{selectedProduct.pcs_per_box || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Box:</strong>
                                </td>
                                <td>{selectedProduct.box || "N/A"}</td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Working Sqft per Box:</strong>
                                </td>
                                <td>
                                  {selectedProduct.working_sqft_per_box ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Loading Address:</strong>
                                </td>
                                <td>
                                  {selectedProduct.loading_address || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Manufacturing Company:</strong>
                                </td>
                                <td>
                                  {selectedProduct.manufacturing_company_name ||
                                    "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Average Rating:</strong>
                                </td>
                                <td>
                                  {selectedProduct.average_rating ? (
                                    <span className="badge bg-warning">
                                      {selectedProduct.average_rating} ⭐
                                    </span>
                                  ) : (
                                    "No ratings yet"
                                  )}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Remark:</strong>
                                </td>
                                <td>{selectedProduct.remark || "N/A"}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-sm-6">
                          <h6>Seller Information</h6>
                          <table className="table table-sm">
                            <tbody>
                              <tr>
                                <td>
                                  <strong>Seller Firm:</strong>
                                </td>
                                <td>
                                  {selectedProduct.seller_firm_name || "N/A"}
                                </td>
                              </tr>
                              <tr>
                                <td>
                                  <strong>Mobile Number:</strong>
                                </td>
                                <td>
                                  {selectedProduct.seller_mobile_number ||
                                    "N/A"}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {selectedProduct.pdfs &&
                        selectedProduct.pdfs.length > 0 && (
                          <div className="row mt-3">
                            <div className="col-12">
                              <h6>PDF Documents</h6>
                              <div className="d-flex flex-wrap gap-2">
                                {selectedProduct.pdfs.map((pdf, index) => (
                                  <div
                                    key={pdf.id || index}
                                    className="card"
                                    style={{ width: "200px" }}
                                  >
                                    <div className="card-body p-2">
                                      <div className="d-flex align-items-center mb-2">
                                        <i className="fe fe-file-text text-primary me-2"></i>
                                        <small className="text-muted">
                                          {pdf.pdf_name ||
                                            pdf.alt ||
                                            `PDF ${index + 1}`}
                                        </small>
                                      </div>
                                      <div className="d-flex gap-1">
                                        <a
                                          href={pdf.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="btn btn-sm btn-outline-primary"
                                          title="View PDF"
                                        >
                                          <i className="fe fe-eye"></i>
                                        </a>
                                        <a
                                          href={pdf.url}
                                          download
                                          className="btn btn-sm btn-outline-success"
                                          title="Download PDF"
                                        >
                                          <i className="fe fe-download"></i>
                                        </a>
                                      </div>
                                      {pdf.uploaded_at && (
                                        <small className="text-muted d-block mt-1">
                                          {new Date(
                                            pdf.uploaded_at
                                          ).toLocaleDateString()}
                                        </small>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                      <div className="row mt-3">
                        <div className="col-12">
                          <h6>Status</h6>
                          <div className="d-flex gap-2">
                            <span
                              className={`badge ${
                                selectedProduct.is_active
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {selectedProduct.is_active
                                ? "Active"
                                : "Inactive"}
                            </span>
                            <span
                              className={`badge ${
                                selectedProduct.is_published
                                  ? "bg-info"
                                  : "bg-secondary"
                              }`}
                            >
                              {selectedProduct.is_published
                                ? "Published"
                                : "Draft"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeDetailModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {pdfViewerModal && selectedPdf && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fe fe-file-text me-2"></i>
                  {selectedPdf.alt || "PDF Document"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePdfViewer}
                ></button>
              </div>
              <div className="modal-body p-0">
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                  <div className="d-flex gap-2">
                    <a
                      href={selectedPdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="fe fe-external-link"></i> Open in New Tab
                    </a>
                    <a
                      href={selectedPdf.url}
                      download
                      className="btn btn-sm btn-outline-success"
                    >
                      <i className="fe fe-download"></i> Download
                    </a>
                  </div>
                </div>
                <div style={{ height: "70vh" }}>
                  <iframe
                    src={selectedPdf.url}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                    title={selectedPdf.alt || "PDF Document"}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePdfViewer}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showModal || detailModal || pdfViewerModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default TileList;
